import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { usePreferences } from "@/lib/preferences";
import { ReadAloud } from "@/components/TTS";
import {
  BookOpen, CheckCircle2, ArrowLeft, ArrowRight, Sparkles, Info,
  ListChecks, AlertTriangle, RefreshCw, MessageSquare,
} from "lucide-react";

/** Render a single content block (heading|text|example|steps|template|checklist|warning|exercise_ref). */
function Block({ b, exerciseById }) {
  if (b.kind === "heading") {
    return <h2 className="mt-6 text-xl font-heading font-medium text-fg-primary" data-testid="block-heading">{b.content}</h2>;
  }
  if (b.kind === "text") {
    return (
      <p className={"mt-3 text-base leading-relaxed text-fg-secondary " + (b.plain_applied ? "italic" : "")} data-testid="block-text" data-plain-applied={b.plain_applied ? "true" : "false"}>
        {b.plain_applied && (
          <span className="text-[10px] uppercase tracking-wider text-progress-teal mr-2 not-italic">Plain language</span>
        )}
        {b.content}
      </p>
    );
  }
  if (b.kind === "example") {
    return (
      <div className="mt-4 rounded-md border border-progress-teal/30 bg-progress-teal/5 p-4" data-testid="block-example">
        <p className="text-[11px] uppercase tracking-wider text-progress-teal">Example</p>
        <p className="mt-1 text-sm text-fg-primary font-medium">{b.content?.title}</p>
        <p className="mt-2 text-sm text-fg-secondary whitespace-pre-line">{b.content?.body}</p>
      </div>
    );
  }
  if (b.kind === "steps") {
    return (
      <ol className="mt-3 list-decimal pl-5 space-y-1 text-sm text-fg-primary" data-testid="block-steps">
        {(b.content || []).map((s, i) => <li key={i}>{s}</li>)}
      </ol>
    );
  }
  if (b.kind === "template") {
    return (
      <div className="mt-4 rounded-md border border-border-subtle bg-bg-base p-4 font-mono text-xs text-fg-primary whitespace-pre-line" data-testid="block-template">
        <p className="not-italic text-[11px] uppercase tracking-wider text-fg-secondary font-sans mb-2">
          {b.content?.title}
        </p>
        {b.content?.body}
      </div>
    );
  }
  if (b.kind === "checklist") {
    return (
      <ul className="mt-3 space-y-1 text-sm text-fg-secondary" data-testid="block-checklist">
        {(b.content || []).map((s, i) => (
          <li key={i} className="flex items-start gap-2">
            <input
              type="checkbox"
              id={`checklist-item-${i}-${(s || "").slice(0, 30).replace(/\s+/g, "-")}`}
              className="mt-1 accent-progress-teal"
              aria-label={`Check off: ${s}`}
            />
            <label htmlFor={`checklist-item-${i}-${(s || "").slice(0, 30).replace(/\s+/g, "-")}`} className="cursor-pointer">{s}</label>
          </li>
        ))}
      </ul>
    );
  }
  if (b.kind === "warning") {
    return (
      <div className="mt-4 rounded-md border border-accent-warm/40 bg-accent-warm/5 p-3 flex gap-2" data-testid="block-warning">
        <AlertTriangle className="h-4 w-4 text-accent-warm flex-shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-sm text-fg-primary">{b.content}</p>
      </div>
    );
  }
  if (b.kind === "exercise_ref") {
    const ex = exerciseById?.[b.content];
    return (
      <div className="mt-6 rounded-lg border border-accent-warm/40 bg-accent-warm/5 p-4" data-testid={`block-exercise-ref-${b.content}`}>
        <p className="text-[11px] uppercase tracking-wider text-accent-warm">Exercise</p>
        <p className="mt-1 text-sm text-fg-primary font-medium">{ex?.title || b.content}</p>
      </div>
    );
  }
  return null;
}

export default function ModulePage() {
  const { moduleId } = useParams();
  const nav = useNavigate();
  const { prefs } = usePreferences();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkAnswers, setCheckAnswers] = useState({});
  const [checkResult, setCheckResult] = useState(null);
  const [checkBusy, setCheckBusy] = useState(false);
  const [helperOpen, setHelperOpen] = useState(false);
  const [helperKind, setHelperKind] = useState("explain_simply");
  const [helperResp, setHelperResp] = useState(null);
  const [helperBusy, setHelperBusy] = useState(false);
  const [helperErr, setHelperErr] = useState(null);
  const [completing, setCompleting] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const { data: d } = await api.get(`/courses/modules/${moduleId}`);
      setData(d);
      // Fire and forget: mark started
      api.post(`/courses/lessons/${moduleId}/start`).catch(() => {});
    } catch (err) {
      setError(extractApiError(err, "Failed to load this module."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [moduleId]);

  const exerciseById = useMemo(() => {
    const m = {};
    for (const ex of data?.module?.exercises || []) m[ex.id] = ex;
    return m;
  }, [data]);

  const check = data?.module?.comprehension;
  const progress = data?.progress || null;
  const passed = !!progress?.comprehension_passed_at;
  const [acceptedExerciseIds, setAcceptedExerciseIds] = useState([]);

  useEffect(() => {
    // Load accepted exercises for the module
    (async () => {
      if (!data?.module?.exercises?.length) return;
      const accepted = new Set();
      for (const ex of data.module.exercises) {
        try {
          const { data: r } = await api.get(`/courses/exercises/${ex.id}/submissions`);
          if ((r.submissions || []).some((s) => s.valid)) accepted.add(ex.id);
        } catch { /* ignore */ }
      }
      setAcceptedExerciseIds([...accepted]);
    })();
  }, [data]);

  const submitCheck = async () => {
    setCheckBusy(true);
    setCheckResult(null);
    try {
      const { data: r } = await api.post(
        `/courses/lessons/${moduleId}/comprehension`,
        { answers: checkAnswers },
      );
      setCheckResult(r);
      if (r.passed) toast.success("Comprehension check passed.");
      else toast.error("Not quite — try again.");
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Submit failed."));
    } finally {
      setCheckBusy(false);
    }
  };

  const completeLesson = async () => {
    setCompleting(true);
    try {
      const { data: r } = await api.post(`/courses/lessons/${moduleId}/complete`);
      setData((d) => ({ ...d, progress: r.progress }));
      toast.success("Lesson marked complete.");
    } catch (err) {
      toast.error(extractApiError(err, "Cannot mark complete yet."));
    } finally {
      setCompleting(false);
    }
  };

  const runHelper = async () => {
    setHelperBusy(true);
    setHelperErr(null);
    try {
      const { data: r } = await api.post("/courses/helpers/course-helper", {
        module_id: moduleId, helper_type: helperKind,
      });
      setHelperResp(r);
    } catch (err) {
      setHelperErr(extractApiError(err, "Helper unavailable right now."));
    } finally {
      setHelperBusy(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary" data-testid="module-loading">
          Loading module…
        </div>
      </AppShell>
    );
  }

  if (error || !data?.module) {
    return (
      <AppShell>
        <div className="mx-auto max-w-3xl px-4 py-14">
          <Alert variant="destructive"><AlertDescription data-testid="module-error">{error || "Module not found."}</AlertDescription></Alert>
          <Link to="/learning"><Button variant="ghost" className="mt-4 text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"><ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />Back to learning</Button></Link>
        </div>
      </AppShell>
    );
  }

  const module = data.module;
  const canComplete = (check ? passed : true) && (
    (module.exercises || []).length === 0 || acceptedExerciseIds.length >= 1
  );
  const completed = progress?.status === "completed";

  return (
    <AppShell>
      <article className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/learning" className="text-xs text-fg-secondary hover:text-fg-primary transition-colors" data-testid="module-back-link">
          <ArrowLeft className="h-3 w-3 inline mr-1" aria-hidden="true" />
          Back to learning
        </Link>

        <header className="mt-4">
          <p className="text-[11px] uppercase tracking-wider text-fg-secondary flex items-center gap-2">
            <BookOpen className="h-3 w-3" aria-hidden="true" />
            Lesson {module.order} · {module.reading_time_min ?? "—"} min read
            {module.is_exemplary && (
              <span data-testid="module-exemplary-badge" className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-progress-teal/40 text-progress-teal bg-progress-teal/10">
                Deep-dive lesson
              </span>
            )}
          </p>
          <h1 className="mt-1 text-3xl sm:text-4xl font-heading font-medium text-fg-primary" data-testid="module-title">{module.title}</h1>
          <p className="mt-3 text-sm text-fg-secondary">{module.description}</p>

          {/* M-W7 Stage 3: Learning objective (owner req #1) */}
          {module.learning_objective && (
            <div data-testid="module-learning-objective" className="mt-4 rounded-md border border-accent-warm/30 bg-accent-warm/5 p-4">
              <p className="text-[11px] uppercase tracking-wider text-accent-warm">Learning objective</p>
              <p className="mt-1 text-sm text-fg-primary leading-relaxed">{module.learning_objective}</p>
            </div>
          )}

          {/* M-W7 Stage 3: Skill-gap match banner (owner req #6).
              Deterministic, honest absence — banner hidden when no match. */}
          {Array.isArray(data.addresses_gaps) && data.addresses_gaps.length > 0 && (
            <div data-testid="module-addresses-gaps" className="mt-3 rounded-md border border-progress-teal/40 bg-progress-teal/5 p-4">
              <p className="text-[11px] uppercase tracking-wider text-progress-teal">This lesson addresses your gap</p>
              <p className="mt-1 text-sm text-fg-primary">
                Based on your confirmed Lane of Direction, this lesson covers:{" "}
                {data.addresses_gaps.map((t, i) => (
                  <span key={t} data-testid={`module-gap-tag-${t}`}>
                    <span className="text-fg-primary font-medium">{t.replace(/_/g, " ")}</span>
                    {i < data.addresses_gaps.length - 1 ? ", " : ""}
                  </span>
                ))}.
              </p>
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <ReadAloud
              text={module.blocks
                .filter((b) => b.kind === "text" || b.kind === "example")
                .map((b) => (b.kind === "example" ? `${b.content?.title}. ${b.content?.body}` : b.content))
                .join(". ")}
              testidPrefix="module-tts"
            />
            <Button
              size="sm" variant="ghost"
              onClick={() => setHelperOpen((v) => !v)}
              data-testid="module-helper-toggle"
              className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
            >
              <Sparkles className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
              {helperOpen ? "Hide AI helper" : "AI helper"}
            </Button>
          </div>
          {completed && (
            <p className="mt-3 inline-flex items-center gap-1 text-xs text-progress-teal" data-testid="module-completed-badge">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
              Completed
            </p>
          )}
        </header>

        {/* AI helper panel */}
        {helperOpen && (
          <div className="mt-4 rounded-lg border border-accent-warm/30 bg-accent-warm/5 p-4" data-testid="helper-panel">
            <p className="text-[11px] uppercase tracking-wider text-accent-warm">Definition-only helper</p>
            <p className="mt-1 text-xs text-fg-secondary">
              This helper does not see your work. Ask for a simpler explanation of this module, or a made-up example.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant={helperKind === "explain_simply" ? "default" : "ghost"} onClick={() => setHelperKind("explain_simply")} data-testid="helper-kind-explain" className={helperKind === "explain_simply" ? "bg-accent-warm text-bg-base hover:bg-accent-warm-hover" : "text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"}>Explain more simply</Button>
              <Button size="sm" variant={helperKind === "example" ? "default" : "ghost"} onClick={() => setHelperKind("example")} data-testid="helper-kind-example" className={helperKind === "example" ? "bg-accent-warm text-bg-base hover:bg-accent-warm-hover" : "text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"}>Give me an example</Button>
              <Button size="sm" onClick={runHelper} disabled={helperBusy} data-testid="helper-run-btn" className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover">
                {helperBusy ? "Thinking…" : "Ask"}
              </Button>
            </div>
            {helperErr && <Alert variant="destructive" className="mt-3"><AlertDescription>{helperErr}</AlertDescription></Alert>}
            {helperResp && (
              <div className="mt-3 rounded-md border border-border-subtle bg-bg-base p-3 text-sm text-fg-primary" data-testid="helper-response">
                <p className="text-[11px] uppercase tracking-wider text-fg-secondary">Draft — always review</p>
                <p className="mt-1 whitespace-pre-line">{helperResp.response}</p>
                {helperResp.cached && <p className="mt-2 text-[11px] text-fg-muted italic" data-testid="helper-cached-note">Cached response (no new AI call).</p>}
              </div>
            )}
          </div>
        )}

        {/* Body blocks */}
        <div className="mt-4">
          {module.blocks.map((b, i) => <Block key={i} b={b} exerciseById={exerciseById} />)}
        </div>

        {/* Comprehension check */}
        {check && (
          <section className="mt-8 rounded-lg border border-border-subtle bg-bg-surface p-5" data-testid="comprehension-section">
            <h2 className="text-lg font-heading font-medium text-fg-primary flex items-center gap-2">
              <ListChecks className="h-4 w-4" aria-hidden="true" />
              Comprehension check
            </h2>
            <p className="mt-1 text-xs text-fg-secondary">
              Deterministic. All correct to pass. Unlimited retries — no penalty.
              {passed && <span className="ml-1 text-progress-teal font-medium" data-testid="comprehension-passed">Passed.</span>}
            </p>
            <div className="mt-4 space-y-4">
              {check.questions.map((q) => (
                <div key={q.id} data-testid={`comprehension-q-${q.id}`}>
                  <p className="text-sm text-fg-primary" id={`${q.id}-prompt`}>{q.prompt}</p>
                  <RadioGroup
                    value={String(checkAnswers[q.id] ?? "")}
                    onValueChange={(v) => setCheckAnswers((s) => ({ ...s, [q.id]: parseInt(v) }))}
                    className="mt-2 space-y-1"
                    aria-labelledby={`${q.id}-prompt`}
                  >
                    {q.options.map((opt, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <RadioGroupItem
                          value={String(i)}
                          id={`${q.id}-${i}`}
                          data-testid={`comprehension-q-${q.id}-opt-${i}`}
                          aria-labelledby={`${q.id}-${i}-label`}
                        />
                        <Label
                          id={`${q.id}-${i}-label`}
                          htmlFor={`${q.id}-${i}`}
                          className="text-sm text-fg-secondary cursor-pointer"
                        >
                          {opt}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              ))}
            </div>
            {checkResult && (
              <>
                <p className={"mt-3 text-sm " + (checkResult.passed ? "text-progress-teal" : "text-destructive")} data-testid="comprehension-result">
                  {checkResult.message}
                </p>
                {/* M-W7 Stage 3: per-wrong-answer plain-language feedback */}
                {!checkResult.passed && Array.isArray(checkResult.diagnostic?.wrong) && checkResult.diagnostic.wrong.map((w) => (
                  w.feedback ? (
                    <div key={w.id} data-testid={`comprehension-feedback-${w.id}`} className="mt-2 rounded-md border border-destructive/30 bg-destructive/5 p-3">
                      <p className="text-xs text-destructive"><strong>{check.questions.find((q) => q.id === w.id)?.prompt?.slice(0, 60)}…</strong></p>
                      <p className="mt-1 text-xs text-fg-primary">{w.feedback}</p>
                    </div>
                  ) : null
                ))}
              </>
            )}
            <div className="mt-4">
              <Button
                onClick={submitCheck}
                disabled={checkBusy || Object.keys(checkAnswers).length < check.questions.length}
                data-testid="comprehension-submit-btn"
                className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              >
                {passed ? "Retry" : (checkBusy ? "Checking…" : "Submit answers")}
              </Button>
            </div>
          </section>
        )}

        {/* Exercises */}
        {(module.exercises || []).map((ex) => (
          <ExerciseBlock key={ex.id} exercise={ex} moduleId={moduleId}
                          isAccepted={acceptedExerciseIds.includes(ex.id)}
                          onAccepted={() => setAcceptedExerciseIds((s) => s.includes(ex.id) ? s : [...s, ex.id])} />
        ))}

        {/* M-W7 Stage 3: Artifact form for exemplary lessons (owner req #7).
             The artifact is saved as work_evidence and surfaces on the
             capstone workspace as a pre-fill SUGGESTION (never auto-completed). */}
        {module.artifact_spec && (
          <ArtifactForm moduleId={moduleId} spec={module.artifact_spec} />
        )}

        {/* Complete lesson */}
        <div className="mt-8 rounded-lg border border-border-subtle bg-bg-surface p-5" data-testid="complete-section">
          <p className="text-sm text-fg-primary">Ready to mark this lesson complete?</p>
          <ul className="mt-2 text-xs text-fg-secondary space-y-1">
            {check && (
              <li>
                {passed ? "✓" : "•"} Pass the comprehension check {passed ? "" : "(unlimited retries)"}.
              </li>
            )}
            {(module.exercises || []).length > 0 && (
              <li>
                {acceptedExerciseIds.length >= 1 ? "✓" : "•"} Submit at least one accepted exercise response.
              </li>
            )}
          </ul>
          <Button
            className="mt-4 bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            onClick={completeLesson}
            disabled={!canComplete || completed || completing}
            data-testid="complete-lesson-btn"
          >
            {completed ? "Already complete" : completing ? "Marking…" : "Mark this lesson complete"}
          </Button>
        </div>
      </article>
    </AppShell>
  );
}


function ExerciseBlock({ exercise, moduleId, isAccepted, onAccepted }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [valid, setValid] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [feedbackErr, setFeedbackErr] = useState(null);
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [guidance, setGuidance] = useState(null);
  const [guidanceBusy, setGuidanceBusy] = useState(false);
  const [guidanceErr, setGuidanceErr] = useState(null);
  const [pastSubs, setPastSubs] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/courses/exercises/${exercise.id}/submissions`);
        setPastSubs(data.submissions || []);
      } catch { /* ignore */ }
    })();
  }, [exercise.id]);

  const submit = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post(`/courses/exercises/${exercise.id}/submit`, {
        submission_text: text,
      });
      setValid(data.valid);
      setMsg(data.message);
      if (data.valid) {
        toast.success("Submission accepted.");
        onAccepted?.();
        const { data: r } = await api.get(`/courses/exercises/${exercise.id}/submissions`);
        setPastSubs(r.submissions || []);
      }
    } catch (err) {
      setMsg(extractApiError(err, "Submit failed."));
    } finally {
      setBusy(false);
    }
  };

  const runGuidance = async () => {
    setGuidanceBusy(true); setGuidanceErr(null);
    try {
      const { data } = await api.post("/courses/helpers/exercise-guidance", { exercise_id: exercise.id });
      setGuidance(data);
    } catch (err) {
      setGuidanceErr(extractApiError(err, "Guidance unavailable right now."));
    } finally {
      setGuidanceBusy(false);
    }
  };

  const runFeedback = async () => {
    setFeedbackBusy(true); setFeedbackErr(null);
    try {
      const { data } = await api.post("/courses/helpers/draft-feedback", {
        exercise_id: exercise.id,
        submission_text: text,
      });
      setFeedback(data);
    } catch (err) {
      const msg = extractApiError(err, "Draft feedback unavailable.");
      setFeedbackErr(msg);
    } finally {
      setFeedbackBusy(false);
    }
  };

  return (
    <section id={`exercise-${exercise.id}`} className="mt-6 rounded-lg border border-accent-warm/30 bg-accent-warm/5 p-5" data-testid={`exercise-${exercise.id}`}>
      <p className="text-[11px] uppercase tracking-wider text-accent-warm">Exercise</p>
      <h3 className="mt-1 text-lg font-heading font-medium text-fg-primary" data-testid={`exercise-${exercise.id}-title`}>{exercise.title}</h3>
      <p className="mt-2 text-sm text-fg-secondary whitespace-pre-line">{exercise.prompt}</p>
      {exercise.rubric?.help && (
        <p className="mt-2 text-xs text-fg-muted italic" data-testid={`exercise-${exercise.id}-help`}>{exercise.rubric.help}</p>
      )}
      {isAccepted && (
        <p className="mt-2 inline-flex items-center gap-1 text-xs text-progress-teal" data-testid={`exercise-${exercise.id}-accepted-badge`}>
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
          A previous submission was accepted. You can still refine and resubmit.
        </p>
      )}
      <Textarea
        data-testid={`exercise-${exercise.id}-input`}
        placeholder="Write your response here. Don't paste confidential details."
        maxLength={20000}
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="mt-3 bg-bg-base border-border-subtle text-fg-primary min-h-40"
      />
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          size="sm"
          onClick={submit}
          disabled={busy || !text.trim()}
          data-testid={`exercise-${exercise.id}-submit-btn`}
          className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
        >
          {busy ? "Submitting…" : "Submit"}
        </Button>
        <Button size="sm" variant="ghost" onClick={runGuidance} disabled={guidanceBusy}
                data-testid={`exercise-${exercise.id}-guidance-btn`}
                className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
          <Info className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
          What makes a strong response?
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setShowFeedback((v) => !v)}
                data-testid={`exercise-${exercise.id}-feedback-toggle`}
                className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
          <MessageSquare className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
          {showFeedback ? "Hide draft feedback" : "Get draft AI feedback"}
        </Button>
      </div>
      {msg && (
        <p className={"mt-3 text-sm " + (valid ? "text-progress-teal" : "text-destructive")} data-testid={`exercise-${exercise.id}-message`}>
          {msg}
        </p>
      )}
      {guidance && (
        <div className="mt-3 rounded-md border border-border-subtle bg-bg-base p-3 text-sm text-fg-primary" data-testid={`exercise-${exercise.id}-guidance-response`}>
          <p className="text-[11px] uppercase tracking-wider text-fg-secondary">Guidance — about this exercise, not your work</p>
          <p className="mt-1 whitespace-pre-line">{guidance.response}</p>
          {guidance.cached && <p className="mt-2 text-[11px] text-fg-muted italic">Cached response (no new AI call).</p>}
        </div>
      )}
      {guidanceErr && <Alert variant="destructive" className="mt-3"><AlertDescription>{guidanceErr}</AlertDescription></Alert>}
      {showFeedback && (
        <div className="mt-3 rounded-md border border-progress-teal/30 bg-progress-teal/5 p-3" data-testid={`exercise-${exercise.id}-feedback-panel`}>
          <p className="text-[11px] uppercase tracking-wider text-progress-teal">Draft AI feedback — reviews YOUR work</p>
          <p className="mt-1 text-xs text-fg-secondary">
            Requires AI-processing consent. Feedback is a suggestion — completion is decided by the rubric above.
          </p>
          <Button
            size="sm" onClick={runFeedback} disabled={feedbackBusy || !text.trim() || text.trim().length < 20}
            data-testid={`exercise-${exercise.id}-feedback-run`}
            className="mt-2 bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
          >
            {feedbackBusy ? "Reading…" : "Ask for draft feedback"}
          </Button>
          {feedbackErr && <Alert variant="destructive" className="mt-3"><AlertDescription data-testid={`exercise-${exercise.id}-feedback-error`}>{feedbackErr}</AlertDescription></Alert>}
          {feedback && (
            <div className="mt-3 rounded-md border border-border-subtle bg-bg-base p-3 text-sm text-fg-primary space-y-2" data-testid={`exercise-${exercise.id}-feedback-response`}>
              <p className="text-[11px] uppercase tracking-wider text-fg-secondary italic">{feedback.draft_label}</p>
              <p><span className="text-progress-teal font-medium">Strength:</span> {feedback.strength}</p>
              <p><span className="text-accent-warm font-medium">Suggestion:</span> {feedback.suggestion}</p>
              <p><span className="text-fg-secondary font-medium">Gentle question:</span> {feedback.gentle_question}</p>
            </div>
          )}
        </div>
      )}
      {pastSubs.length > 0 && (
        <details className="mt-4">
          <summary className="text-xs text-fg-muted cursor-pointer" data-testid={`exercise-${exercise.id}-history-summary`}>
            Your past submissions ({pastSubs.length})
          </summary>
          <ul className="mt-2 space-y-2 pl-2 border-l border-border-subtle">
            {pastSubs.map((s) => (
              <li key={s.id} className="text-xs text-fg-secondary pl-3" data-testid={`exercise-${exercise.id}-history-${s.id}`}>
                <p className={s.valid ? "text-progress-teal" : "text-destructive"}>
                  {s.valid ? "Accepted" : "Not yet accepted"} · {new Date(s.created_at).toLocaleString()}
                </p>
                <p className="text-fg-muted italic whitespace-pre-line line-clamp-3">{s.submission_text}</p>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}


// -------------------------------------------------------------------------
//  ArtifactForm — persistent tangible-output form for exemplary lessons
//  (M-W7 Stage 3, owner req #7).
//  Saved via POST /api/lesson-artifacts. Preloaded via
//  GET /api/lesson-artifacts/for-module/{module_id}. Feeds capstone
//  workspace as a pre-fill SUGGESTION — never auto-completes any field.
// -------------------------------------------------------------------------

function ArtifactForm({ moduleId, spec }) {
  const [values, setValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState([]);
  const [saveErr, setSaveErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [loadErr, setLoadErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/lesson-artifacts/for-module/${moduleId}`);
        if (data?.artifact?.fields) {
          setValues(data.artifact.fields);
          setSaved(data.artifact);
        }
      } catch (err) {
        setLoadErr(extractApiError(err, "Couldn't load your saved artifact."));
      }
    })();
  }, [moduleId]);

  const save = async () => {
    setBusy(true);
    setFieldErrors([]);
    setSaveErr(null);
    try {
      const { data } = await api.post("/lesson-artifacts", {
        module_id: moduleId,
        artifact_key: spec.key,
        fields: values,
      });
      setSaved(data.artifact);
      toast.success("Artifact saved to your portfolio.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.field_errors) {
        setFieldErrors(detail.field_errors);
        setSaveErr(detail.message || "A few fields need more detail — see below.");
      } else {
        const msg = extractApiError(err, "Couldn't save the artifact. Nothing was lost — please try again.");
        setSaveErr(msg);
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid={`artifact-form-${moduleId}`}
      className="mt-8 rounded-lg border border-accent-warm/30 bg-accent-warm/5 p-5"
    >
      <p className="text-[11px] uppercase tracking-wider text-accent-warm">Your tangible output</p>
      <h2 className="mt-1 text-lg font-heading font-medium text-fg-primary">{spec.title}</h2>
      <p className="mt-1 text-xs text-fg-secondary">{spec.description}</p>
      {saved && (
        <p data-testid={`artifact-form-${moduleId}-saved-note`} className="mt-2 text-[11px] text-progress-teal italic">
          Saved. You can keep editing — it stays in your portfolio and appears in your capstone workspace.
        </p>
      )}
      {loadErr && <Alert variant="destructive" className="mt-3"><AlertDescription>{loadErr}</AlertDescription></Alert>}

      <div className="mt-4 space-y-4">
        {(spec.fields || []).map((f) => (
          <div key={f.name} data-testid={`artifact-field-${f.name}`}>
            <Label htmlFor={`af-${f.name}`} className="text-xs text-fg-primary font-medium">{f.label}</Label>
            <p className="text-[11px] text-fg-secondary mt-0.5">{f.prompt}</p>
            <Textarea
              id={`af-${f.name}`}
              data-testid={`artifact-field-${f.name}-input`}
              value={values[f.name] || ""}
              onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
              rows={f.min_words > 60 ? 6 : 3}
              className="mt-1 bg-bg-base border-border-subtle text-fg-primary text-sm"
              placeholder={f.example ? `e.g. ${f.example.slice(0, 120)}${f.example.length > 120 ? "…" : ""}` : ""}
            />
            <p className="mt-0.5 text-[10px] text-fg-muted">Aim for at least {f.min_words} words.</p>
          </div>
        ))}
      </div>

      {fieldErrors.length > 0 && (
        <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 p-3" data-testid={`artifact-form-${moduleId}-errors`}>
          <p className="text-xs text-destructive font-medium">A few fields need more detail:</p>
          <ul className="mt-1 space-y-0.5 text-xs text-destructive">
            {fieldErrors.map((e, i) => <li key={i}>· {e}</li>)}
          </ul>
        </div>
      )}

      {saveErr && fieldErrors.length === 0 && (
        <div
          className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 p-3"
          data-testid={`artifact-form-${moduleId}-save-error`}
          role="alert"
        >
          <p className="text-xs text-destructive font-medium">
            Save didn't go through
          </p>
          <p className="mt-1 text-xs text-destructive">{saveErr}</p>
          <p className="mt-1 text-[11px] text-fg-secondary italic">
            Your typed content is still in the form — no data was lost. Try
            Save again in a moment.
          </p>
        </div>
      )}

      <div className="mt-4">
        <Button
          onClick={save}
          disabled={busy}
          data-testid={`artifact-form-${moduleId}-save-btn`}
          className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
        >
          {busy ? "Saving…" : (saved ? "Save changes" : "Save artifact")}
        </Button>
      </div>

      <p className="mt-3 text-[11px] text-fg-muted italic">
        Saved artifacts appear in your capstone workspace as pre-fill
        suggestions — you still confirm every capstone field yourself.
      </p>
    </section>
  );
}

